/* Soundscape mixer page.
 *
 * Multi-layer blending: one color-noise layer + up to two nature layers,
 * each with its own volume slider. Users can play the four curated presets,
 * save custom mixes with a name, and reload / delete their saved mixes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, Timer, Save, Trash2, Sparkles, Volume2, Plus, X } from 'lucide-react';
import api from '@/lib/api';
import { COLORS, COLOR_META } from '@/lib/colorNoise';
import { SoundscapeMixer } from '@/lib/soundscape';
import { resolveNatureTrack, resolveNatureUrl } from '@/lib/natureTracks';
import { track } from '@/lib/analytics';

const NATURE_KEYWORDS = {
    rain:   { label: 'Rain',          tokens: ['rain'] },
    waves:  { label: 'Ocean waves',   tokens: ['ocean', 'waves', 'sea'] },
    forest: { label: 'Forest / wind', tokens: ['wind', 'forest', 'trees', 'rainforest'] },
};

const EMPTY_MIX = {
    name: '',
    layers: [{ kind: 'color', key: 'brown', volume: 0.5, sleep: false }],
};

function pickNatureTrack(catalog, key) { return resolveNatureTrack(catalog, key); }

function fmtCountdown(sec) {
    if (sec == null) return '';
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

export default function Soundscape() {
    const [draft, setDraft] = useState(EMPTY_MIX);
    const [presets, setPresets] = useState([]);
    const [mine, setMine] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [playing, setPlaying] = useState(false);
    const [masterVolume, setMasterVolume] = useState(0.85);
    const [timerMin, setTimerMin] = useState(null);
    const [remaining, setRemaining] = useState(null);
    const [savingName, setSavingName] = useState('');
    const [saving, setSaving] = useState(false);
    const mixerRef = useRef(null);
    const timerHandleRef = useRef(null);

    useEffect(() => {
        api.get('/soundscapes/presets').then((r) => setPresets(r.data || [])).catch(() => {});
        api.get('/soundscapes').then((r) => setMine(r.data || [])).catch(() => {});
        api.get('/music').then((r) => setCatalog(r.data || [])).catch(() => {});
        return () => {
            mixerRef.current?.stop();
            mixerRef.current = null;
            if (timerHandleRef.current) clearInterval(timerHandleRef.current);
        };
    }, []);

    const resolveNatureUrlLocal = useMemo(() => (key) => resolveNatureUrl(catalog, key), [catalog]);

    const colorLayerIndex = draft.layers.findIndex((l) => l.kind === 'color');
    const colorLayer = colorLayerIndex >= 0 ? draft.layers[colorLayerIndex] : null;
    const natureLayers = draft.layers.filter((l) => l.kind === 'nature');

    const updateLayer = (idx, patch) => {
        setDraft((d) => ({
            ...d,
            layers: d.layers.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
        }));
        if (playing) {
            mixerRef.current?.setLayerVolume(idx, patch.volume ?? draft.layers[idx].volume);
        }
    };

    const setColor = (key) => {
        setDraft((d) => ({
            ...d,
            layers: d.layers.map((l) => (l.kind === 'color' ? { ...l, key } : l)),
        }));
        if (playing) restart();
    };

    const toggleSleep = () => {
        setDraft((d) => ({
            ...d,
            layers: d.layers.map((l) => (l.kind === 'color' ? { ...l, sleep: !l.sleep } : l)),
        }));
        if (playing) restart();
    };

    const addNature = (key) => {
        if (natureLayers.length >= 2) return;
        if (draft.layers.some((l) => l.kind === 'nature' && l.key === key)) return;
        setDraft((d) => ({
            ...d,
            layers: [...d.layers, { kind: 'nature', key, volume: 0.4, sleep: false }],
        }));
        if (playing) restart();
    };

    const removeLayer = (idx) => {
        setDraft((d) => ({
            ...d,
            layers: d.layers.filter((_, i) => i !== idx),
        }));
        if (playing) restart();
    };

    const _ensureMixer = () => {
        if (!mixerRef.current) mixerRef.current = new SoundscapeMixer(resolveNatureUrlLocal);
        return mixerRef.current;
    };

    const startMix = async () => {
        const m = _ensureMixer();
        m.setMasterBase(masterVolume);
        m.setDuckRatio(1.0);
        await m.play(draft.layers);
        setPlaying(true);
        track('soundscape-played', { layers: draft.layers.length, timer: timerMin });

        if (timerMin) {
            const endsAt = Date.now() + timerMin * 60_000;
            setRemaining(timerMin * 60);
            if (timerHandleRef.current) clearInterval(timerHandleRef.current);
            timerHandleRef.current = setInterval(() => {
                const rem = Math.round((endsAt - Date.now()) / 1000);
                if (rem <= 0) {
                    clearInterval(timerHandleRef.current);
                    timerHandleRef.current = null;
                    setRemaining(null);
                    stopMix();
                } else setRemaining(rem);
            }, 500);
        } else {
            setRemaining(null);
        }
    };

    const stopMix = () => {
        mixerRef.current?.stop({ keepContext: true });
        setPlaying(false);
        if (timerHandleRef.current) clearInterval(timerHandleRef.current);
        timerHandleRef.current = null;
        setRemaining(null);
    };

    const restart = async () => {
        if (!mixerRef.current) return;
        mixerRef.current.setMasterBase(masterVolume);
        await mixerRef.current.play(draft.layers);
    };

    const togglePlay = () => (playing ? stopMix() : startMix());

    const onMasterVolume = (v) => {
        setMasterVolume(v);
        mixerRef.current?.setMasterBase(v);
    };

    const loadMix = (mix) => {
        stopMix();
        setDraft({
            name: mix.name,
            layers: mix.layers.map((l) => ({ ...l })),
        });
    };

    const deleteMix = async (mix) => {
        if (!window.confirm(`Delete “${mix.name}”?`)) return;
        await api.delete(`/soundscapes/${mix.id}`);
        setMine((xs) => xs.filter((x) => x.id !== mix.id));
    };

    const saveMix = async () => {
        const name = (savingName || draft.name || '').trim();
        if (!name) return;
        setSaving(true);
        try {
            const { data } = await api.post('/soundscapes', {
                name,
                layers: draft.layers,
            });
            setMine((xs) => [data, ...xs]);
            setDraft((d) => ({ ...d, name }));
            setSavingName('');
            track('soundscape-saved', { name, layers: draft.layers.length });
        } finally {
            setSaving(false);
        }
    };

    const availableNatures = Object.keys(NATURE_KEYWORDS).filter(
        (k) => !draft.layers.some((l) => l.kind === 'nature' && l.key === k)
    );

    return (
        <div className="space-y-8" data-testid="soundscape-page">
            <header className="space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-forest-soft">Soundscape Mixer</p>
                <h1 className="font-serif text-4xl sm:text-5xl text-forest">Build your own sound bath</h1>
                <p className="text-forest-soft max-w-xl">
                    Layer a colour of noise beneath nature loops. Save your favourite blends by name — they&rsquo;ll show up here for one-tap play.
                </p>
            </header>

            {/* Presets */}
            <section className="qm-card p-6 md:p-8" data-testid="preset-section">
                <h2 className="font-serif text-2xl text-forest mb-4 flex items-center gap-2">
                    <Sparkles size={18} /> Curated presets
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {presets.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => loadMix(p)}
                            data-testid={`preset-${p.id}`}
                            className="text-left qm-card p-4 hover:shadow-md transition border border-transparent hover:border-forest/20"
                        >
                            <div className="font-serif text-lg text-forest">{p.name}</div>
                            <p className="text-xs text-forest-soft mt-1 line-clamp-2">{p.description}</p>
                            <p className="text-[11px] text-forest-soft mt-2">
                                {p.layers.length} layer{p.layers.length === 1 ? '' : 's'}
                            </p>
                        </button>
                    ))}
                </div>
            </section>

            {/* Mix editor */}
            <section className="qm-card p-6 md:p-8" data-testid="mix-editor">
                <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                    <div>
                        <h2 className="font-serif text-2xl text-forest">Your mix</h2>
                        <p className="text-sm text-forest-soft">{draft.name || 'Untitled'} · {draft.layers.length} layer{draft.layers.length === 1 ? '' : 's'}</p>
                    </div>
                    <button
                        type="button"
                        onClick={togglePlay}
                        data-testid={playing ? 'mix-stop' : 'mix-play'}
                        className="qm-btn-primary inline-flex items-center gap-2"
                    >
                        {playing ? <Pause size={16} /> : <Play size={16} />}
                        {playing ? 'Stop mix' : 'Play mix'}
                    </button>
                </div>

                {/* Color layer */}
                {colorLayer && (
                    <div className="rounded-3xl bg-cream/60 border border-forest/10 p-4 mb-3" data-testid="color-layer">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-forest-soft">Colour layer</div>
                                <div className="font-serif text-xl text-forest">{COLOR_META[colorLayer.key].label} noise{colorLayer.sleep ? ' · sleep pulses' : ''}</div>
                            </div>
                            <label className="flex items-center gap-2 text-xs text-forest-soft cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={colorLayer.sleep}
                                    onChange={toggleSleep}
                                    data-testid="color-sleep-toggle"
                                />
                                Sleep pulses
                            </label>
                        </div>
                        <div className="grid grid-cols-7 gap-2 mb-3">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    data-testid={`layer-color-${c}`}
                                    aria-pressed={colorLayer.key === c}
                                    className={`rounded-2xl border p-2 text-center transition ${colorLayer.key === c ? 'border-forest ring-2 ring-forest/30' : 'border-transparent hover:border-forest/30'}`}
                                    style={{ background: `linear-gradient(160deg, ${COLOR_META[c].hue} 0%, rgba(250,247,242,0.9) 120%)` }}
                                >
                                    <div className="text-[11px] font-medium text-forest">{COLOR_META[c].label}</div>
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <Volume2 size={14} className="text-forest-soft" />
                            <input
                                type="range" min="0" max="1" step="0.02"
                                value={colorLayer.volume}
                                onChange={(e) => updateLayer(colorLayerIndex, { volume: parseFloat(e.target.value) })}
                                data-testid="color-layer-volume"
                                className="w-full accent-forest"
                            />
                            <span className="text-xs text-forest-soft w-10 text-right tabular-nums">{Math.round(colorLayer.volume * 100)}%</span>
                        </div>
                    </div>
                )}

                {/* Nature layers */}
                {draft.layers.map((layer, idx) => {
                    if (layer.kind !== 'nature') return null;
                    const meta = NATURE_KEYWORDS[layer.key];
                    const t = pickNatureTrack(catalog, layer.key);
                    return (
                        <div key={`${layer.key}-${idx}`} className="rounded-3xl bg-cream/60 border border-forest/10 p-4 mb-3" data-testid={`nature-layer-${layer.key}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-forest-soft">Nature layer</div>
                                    <div className="font-serif text-xl text-forest">{meta?.label || layer.key}</div>
                                    {t && <div className="text-xs text-forest-soft">{t.title}</div>}
                                    {!t && <div className="text-xs text-red-700">Not available yet</div>}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeLayer(idx)}
                                    data-testid={`remove-layer-${layer.key}`}
                                    aria-label="Remove layer"
                                    className="rounded-full w-9 h-9 grid place-items-center text-forest hover:bg-dusty/40"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                <Volume2 size={14} className="text-forest-soft" />
                                <input
                                    type="range" min="0" max="1" step="0.02"
                                    value={layer.volume}
                                    onChange={(e) => updateLayer(idx, { volume: parseFloat(e.target.value) })}
                                    data-testid={`nature-layer-volume-${layer.key}`}
                                    className="w-full accent-forest"
                                />
                                <span className="text-xs text-forest-soft w-10 text-right tabular-nums">{Math.round(layer.volume * 100)}%</span>
                            </div>
                        </div>
                    );
                })}

                {/* Add nature layer */}
                {availableNatures.length > 0 && natureLayers.length < 2 && (
                    <div className="flex items-center flex-wrap gap-2 mt-3" data-testid="add-nature-row">
                        <span className="text-sm text-forest-soft inline-flex items-center gap-1">
                            <Plus size={14} /> Add nature layer:
                        </span>
                        {availableNatures.map((key) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => addNature(key)}
                                data-testid={`add-nature-${key}`}
                                className="qm-pill text-sm border border-forest/20 bg-white/60 text-forest hover:bg-sage/30"
                            >
                                {NATURE_KEYWORDS[key].label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Master volume */}
                <div className="mt-6 flex items-center gap-3">
                    <Volume2 size={16} className="text-forest-soft" />
                    <input
                        type="range" min="0" max="1" step="0.02"
                        value={masterVolume}
                        onChange={(e) => onMasterVolume(parseFloat(e.target.value))}
                        data-testid="mix-master-volume"
                        className="w-full accent-forest"
                        aria-label="Master volume"
                    />
                    <span className="text-xs text-forest-soft w-10 text-right tabular-nums">Master {Math.round(masterVolume * 100)}%</span>
                </div>

                {/* Timer */}
                <div className="mt-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-forest-soft mb-2">Timer</div>
                    <div className="flex flex-wrap gap-2" data-testid="mix-timer-options">
                        {[null, 15, 30, 45, 60, 90].map((m) => (
                            <button
                                key={m ?? 'inf'}
                                type="button"
                                onClick={() => setTimerMin(m)}
                                data-testid={`mix-timer-${m ?? 'off'}`}
                                aria-pressed={timerMin === m}
                                className={`qm-pill text-sm ${timerMin === m ? 'bg-forest text-cream border-transparent' : 'bg-white/60 border-forest/20 text-forest hover:bg-sage/30'}`}
                            >
                                {m == null ? 'Until I stop' : `${m} min`}
                            </button>
                        ))}
                    </div>
                    {playing && timerMin != null && remaining != null && (
                        <p className="text-xs text-forest-soft mt-2" data-testid="mix-countdown">
                            <Timer size={12} className="inline mr-1" /> {fmtCountdown(remaining)} remaining
                        </p>
                    )}
                </div>

                {/* Save mix */}
                <div className="mt-6 border-t border-forest/10 pt-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-forest-soft mb-2">Save this mix</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <input
                            type="text"
                            value={savingName}
                            onChange={(e) => setSavingName(e.target.value)}
                            placeholder="e.g. Rainy study afternoon"
                            data-testid="mix-name-input"
                            className="qm-input flex-1 min-w-[220px]"
                            maxLength={60}
                        />
                        <button
                            type="button"
                            onClick={saveMix}
                            disabled={!savingName.trim() || saving}
                            data-testid="mix-save-button"
                            className="qm-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            <Save size={14} /> {saving ? 'Saving…' : 'Save mix'}
                        </button>
                    </div>
                </div>
            </section>

            {/* My saved mixes */}
            <section className="qm-card p-6 md:p-8" data-testid="my-mixes-section">
                <h2 className="font-serif text-2xl text-forest mb-4">Your saved mixes</h2>
                {mine.length === 0 ? (
                    <p className="text-forest-soft text-sm">Save a blend above and it&rsquo;ll live here for next time.</p>
                ) : (
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-mixes-list">
                        {mine.map((m) => (
                            <li key={m.id} className="qm-card p-4 flex flex-col gap-2">
                                <div className="font-serif text-lg text-forest">{m.name}</div>
                                <div className="text-xs text-forest-soft">
                                    {m.layers.map((l) => (l.kind === 'color' ? COLOR_META[l.key]?.label : NATURE_KEYWORDS[l.key]?.label)).join(' · ')}
                                </div>
                                <div className="mt-auto flex items-center gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => loadMix(m)}
                                        data-testid={`load-mix-${m.id}`}
                                        className="qm-btn-primary inline-flex items-center gap-1.5 text-sm py-2 px-4"
                                    >
                                        <Play size={14} /> Load
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deleteMix(m)}
                                        data-testid={`delete-mix-${m.id}`}
                                        aria-label={`Delete ${m.name}`}
                                        className="ml-auto rounded-full w-9 h-9 grid place-items-center text-forest hover:bg-dusty/40"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
